/*
 * The one place that assembles "ArticuLearn - <what this page is>", and the one
 * place that writes document.title on the router's behalf.
 *
 * The brand half stays a constant rather than a locale key on purpose. It is a
 * proper noun, so a key for it would ship 31 byte-identical values, which is
 * precisely what the parity gate reports as untranslated copy (and what
 * scripts/localesTranslate.js refuses to splice). The localized half is passed
 * in by the caller, already through `t()`.
 *
 * WHO OWNS A TITLE. Exactly one writer per route:
 *   - a route with `meta.titleKey` is owned HERE (the dashboard tabs, 404);
 *   - a route whose view calls `useHead({ title })` is owned by that view
 *     (Home, About, FAQ, delete-account, the legal pages, both blog pages),
 *     and those titles are `computed`, so they follow the locale themselves.
 * Both writing the same route is the bug this file exists to avoid: on a blog
 * post the view's title is the English article name, and a router that also
 * claimed that route would replace it with "ArticuLearn - Blogs" the moment the
 * reader switched language.
 */

import { watch } from "vue";

export const BRAND_NAME = "ArticuLearn";

/**
 * @param {string} [suffix] the localized page name.
 * @returns {string} "ArticuLearn - <suffix>", or the brand alone when the
 *   suffix is missing or blank. A blank suffix would otherwise render a
 *   dangling separator, which is what a not-yet-resolved async page name looks
 *   like.
 */
export function brandedTitle(suffix) {
  const trimmed = typeof suffix === "string" ? suffix.trim() : "";
  return trimmed ? `${BRAND_NAME} - ${trimmed}` : BRAND_NAME;
}

/**
 * The document title for a route, in the reader's language.
 *
 * A function of (route, translate) rather than a statement inside a navigation
 * guard, so it can be driven directly in a test.
 *
 * @param {{meta?: {titleKey?: string}}} route
 * @param {(key: string) => string} translate typically `i18n.global.t`.
 */
export function documentTitleFor(route, translate) {
  const key = route?.meta?.titleKey;
  return key ? brandedTitle(translate(key)) : BRAND_NAME;
}

/**
 * Keep document.title in step with both the route and the locale.
 *
 * TWO triggers, and the second is the one that is easy to miss. Locale bundles
 * load lazily, so a cold boot on a saved non-English locale resolves the first
 * title while i18n is still on the en-us fallback; and switching language from
 * the switcher does not navigate at all. A route-only hook leaves an English
 * tab in both cases, which is the defect this whole unit is about.
 *
 * `afterEach`, not `beforeEach`: that guard also runs every auth check, and a
 * throw inside it breaks all navigation. Nothing about a tab title is worth
 * that risk, and afterEach cannot cancel or redirect anything.
 *
 * @param {{afterEach: Function, currentRoute: {value: object}}} router
 * @param {{global: {locale: object, t: Function}}} i18n
 * @returns {Function} the apply function, for tests and for callers that want
 *   to force a refresh.
 */
export function installDocumentTitle(router, i18n) {
  const apply = () => {
    if (typeof document === "undefined") return;
    // t() returns the key rather than throwing when a bundle has not loaded
    // yet, so a missing message degrades to a visible key, never to an error.
    document.title = documentTitleFor(router.currentRoute.value, (key) =>
      i18n.global.t(key)
    );
  };

  // A NAVIGATION always writes, including for a view-owned route: the tab must
  // not keep the previous page's name while the new view mounts, so it resets
  // to the brand and the view's own useHead replaces it a tick later.
  router.afterEach(apply);

  // A LOCALE CHANGE writes only for the routes this module owns. A view-owned
  // title is already locale-reactive (its useHead value is a computed), and on
  // a blog post it is the English article name -- writing the brand over it
  // would blank the article name out of the tab the moment the reader switched
  // language, which is a regression, not a fix.
  watch(
    i18n.global.locale,
    () => {
      if (!router.currentRoute.value?.meta?.titleKey) return;
      apply();
    },
    { immediate: true }
  );
  return apply;
}
